
<template>
  <div class="page">
    <!-- HEADER -->
    <div class="page-head">
      <div>
        <h1>Repayments</h1>
      </div>

      <div class="actions">
        <div class="search-mini">
          <i class="bx bx-search"></i>
          <input v-model="query" placeholder="Search name, phone, loanId, userId..." />
        </div>

        <select v-model="statusFilter" class="select">
          <option value="">All Status</option>
          <option value="DUE">Due</option>
          <option value="PAID">Paid</option>
          <option value="OVERDUE">Overdue</option>
        </select>

        <select v-model="bucketFilter" class="select">
          <option value="">All Buckets</option>
          <option value="TODAY">Due Today</option>
          <option value="7D">Next 7 days</option>
          <option value="30D">Next 30 days</option>
        </select>

        <select v-model="sortBy" class="select">
          <option value="DATE_ASC">Date (Soonest)</option>
          <option value="DATE_DESC">Date (Latest)</option>
          <option value="AMOUNT_DESC">Amount High → Low</option>
          <option value="AMOUNT_ASC">Amount Low → High</option>
        </select>

        <button class="btn ghost" :disabled="loading.export" @click="exportXl">
          <span v-if="loading.export" class="loader" style="margin-right:8px;"></span>
          <i v-else class="bx bx-export"></i>
          {{ loading.export ? "Exporting..." : "Export" }}
        </button>
      </div>
    </div>

    <!-- SUMMARY -->
    <div class="grid cols-4" style="margin-top: 10px;">
      <div class="card stat">
        <div class="card-title">
          <strong>Due Today</strong>
          <span class="badge warning">NOW</span>
        </div>
        <div class="metric">
          <span v-if="loading.stats">--</span>
          <span v-else>{{ dueToday }}</span>
        </div>
        <div class="mini">Installments scheduled today</div>
      </div>

      <div class="card stat">
        <div class="card-title">
          <strong>Overdue</strong>
          <span class="badge warning">RISK</span>
        </div>
        <div class="metric">
          <span v-if="loading.stats">--</span>
          <span v-else>{{ overdue }}</span>
        </div>
        <div class="mini">Requires follow-up</div>
      </div>

      <div class="card stat">
        <div class="card-title">
          <strong>Collected (30d)</strong>
          <span class="badge success">OK</span>
        </div>
        <div class="metric">
          <span v-if="loading.stats">₹ --</span>
          <span v-else>₹ {{ fmt(collected30d) }}</span>
        </div>
        <div class="mini">Paid installments</div>
      </div>

      <div class="card stat">
        <div class="card-title">
          <strong>Pending Amount</strong>
          <span class="badge primary">QUEUE</span>
        </div>
        <div class="metric">
          <span v-if="loading.stats">₹ --</span>
          <span v-else>₹ {{ fmt(pendingAmount) }}</span>
        </div>
        <div class="mini">Due + Overdue totals</div>
      </div>
    </div>

    <!-- LIST -->
    <div class="card" style="margin-top: 14px;">
      <div class="table-head">
        <div class="table-title">
          <strong>Repayment Schedule</strong>
          <span class="muted">Showing {{ filtered.length }} entries</span>
        </div>

        <button class="btn primary" :disabled="loading.list" @click="toast.info('Bulk reminders later')">
          <i class="bx bx-bell"></i>
          Send Reminder
        </button>
      </div>

      <!-- Desktop table -->
      <div class="table-wrap desktop-only">
        <table class="table">
          <thead>
            <tr>
              <th>LOAN</th>
              <th>USER</th>
              <th>DUE DATE</th>
              <th>AMOUNT</th>
              <th>STATUS</th>
              <th>PAYMENT</th>
              <th style="text-align:right;">ACTIONS</th>
            </tr>
          </thead>

          <tbody>
            <tr v-if="loading.list">
              <td colspan="7" style="padding:18px;">
                <div class="empty">
                  <i class="bx bx-loader-alt bx-spin"></i>
                  <div>
                    <strong>Loading repayments...</strong>
                    <div class="row-muted">Please wait</div>
                  </div>
                </div>
              </td>
            </tr>

            <tr
              v-for="r in filtered"
              v-else
              :key="r.installmentId"
              :class="{ dangerRow: r.status === 'OVERDUE' }"
            >
              <td>
                <div class="loan">
                  <strong>#{{ r.loanId }}</strong>
                  <span class="row-muted">Inst #{{ r.installmentNo }} • ID {{ r.installmentId }}</span>
                </div>
              </td>

              <td>
                <div class="usr">
                  <div class="avatar">{{ (r.name || "U").charAt(0).toUpperCase() }}</div>
                  <div class="usr-meta">
                    <div class="usr-name">{{ r.name }}</div>
                    <div class="row-muted">User #{{ r.userId }} • {{ r.phone }}</div>
                  </div>
                </div>
              </td>

              <td>
                <div class="date">
                  <strong>{{ r.dueDate }}</strong>
                  <span class="pill tiny" :class="bucketClass(r.bucket)">
                    <i class="bx" :class="bucketIcon(r.bucket)"></i>
                    {{ bucketLabel(r.bucket) }}
                  </span>
                </div>
              </td>

              <td>
                <div class="amt">
                  <strong>₹ {{ fmt(r.amount) }}</strong>
                  <span class="row-muted">Penalty: ₹ {{ fmt(r.penalty) }}</span>
                </div>
              </td>

              <td>
                <span class="status-pill" :class="statusClass(r.status)">
                  <i class="bx" :class="statusIcon(r.status)"></i>
                  {{ r.status }}
                </span>
              </td>

              <td class="row-muted">
                <div v-if="r.status === 'PAID'" class="payMeta">
                  <div>Txn: <b>{{ r.txnId }}</b></div>
                  <div>{{ r.paidAt }} • {{ r.method }}</div>
                </div>
                <span v-else>-</span>
              </td>

              <td style="text-align:right;">
                <div class="row-actions">
                  <button class="icon-mini" @click="openDrawer(r)" title="Details">
                    <i class="bx bx-show"></i>
                  </button>

                  <button v-if="r.status !== 'PAID'" class="icon-mini" @click="openPaid(r)" title="Mark Paid">
                    <i class="bx bx-check-circle"></i>
                  </button>

                  <button v-if="r.status === 'PAID'" class="icon-mini" @click="openReceipt(r)" title="Receipt">
                    <i class="bx bx-receipt"></i>
                  </button>

                  <button class="icon-mini" @click="handleReminder(r)" title="Send reminder">
                    <i class="bx bx-bell"></i>
                  </button>
                </div>
              </td>
            </tr>

            <tr v-if="!loading.list && filtered.length === 0">
              <td colspan="7" style="padding: 18px;">
                <div class="empty">
                  <i class="bx bx-calendar-x"></i>
                  <div>
                    <strong>No repayments found</strong>
                    <div class="row-muted">Try adjusting search/filters.</div>
                  </div>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <!-- Mobile cards -->
      <div class="mobile-only list-cards">
        <div v-if="loading.list" class="r-card">
          <div class="empty">
            <i class="bx bx-loader-alt bx-spin"></i>
            <div>
              <strong>Loading repayments...</strong>
              <div class="row-muted">Please wait</div>
            </div>
          </div>
        </div>

        <div
          v-for="r in filtered"
          v-else
          :key="r.installmentId"
          class="r-card"
          :class="{ dangerCard: r.status === 'OVERDUE' }"
        >
          <div class="top">
            <div class="loan">
              <strong>#{{ r.loanId }}</strong>
              <span class="row-muted">Inst #{{ r.installmentNo }}</span>
            </div>

            <span class="status-pill" :class="statusClass(r.status)">
              <i class="bx" :class="statusIcon(r.status)"></i>
              {{ r.status }}
            </span>
          </div>

          <div class="mid">
            <div class="usr">
              <div class="avatar">{{ (r.name || "U").charAt(0).toUpperCase() }}</div>
              <div class="usr-meta">
                <div class="usr-name">{{ r.name }}</div>
                <div class="row-muted">{{ r.phone }}</div>
              </div>
            </div>

            <div class="row2">
              <div class="date">
                <strong>{{ r.dueDate }}</strong>
                <span class="pill tiny" :class="bucketClass(r.bucket)">
                  <i class="bx" :class="bucketIcon(r.bucket)"></i>
                  {{ bucketLabel(r.bucket) }}
                </span>
              </div>

              <div class="amt">
                <strong>₹ {{ fmt(r.amount) }}</strong>
                <span class="row-muted">Penalty: ₹ {{ fmt(r.penalty) }}</span>
              </div>
            </div>

            <div v-if="r.status === 'PAID'" class="paidBox">
              <i class="bx bx-check-shield"></i>
              <div>
                <strong>Paid</strong>
                <div class="row-muted">Txn: {{ r.txnId }} • {{ r.method }}</div>
              </div>
            </div>
          </div>

          <div class="card-actions">
            <button class="btn ghost" @click="openDrawer(r)">
              <i class="bx bx-show"></i> Details
            </button>

            <button v-if="r.status !== 'PAID'" class="btn ghost" @click="openPaid(r)">
              <i class="bx bx-check-circle"></i> Mark Paid
            </button>

            <button v-if="r.status === 'PAID'" class="btn ghost" @click="openReceipt(r)">
              <i class="bx bx-receipt"></i> Receipt
            </button>

            <button class="btn ghost" @click="handleReminder(r)">
              <i class="bx bx-bell"></i> Reminder
            </button>
          </div>
        </div>

        <div v-if="!loading.list && filtered.length === 0" class="r-card">
          <div class="empty">
            <i class="bx bx-calendar-x"></i>
            <div>
              <strong>No repayments found</strong>
              <div class="row-muted">Try adjusting search/filters.</div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- DETAILS DRAWER -->
    <div v-if="drawer.open" class="drawer-backdrop" @click.self="closeDrawer">
      <aside class="drawer" v-motion-pop>
        <div class="drawer-head">
          <div class="drawer-title">
            <strong>Repayment Details</strong>
            <span class="row-muted">Installment audit view</span>
          </div>
          <button class="icon-mini" @click="closeDrawer"><i class="bx bx-x"></i></button>
        </div>

        <div class="drawer-body">
          <div class="detail-card">
            <div class="dtop">
              <div>
                <strong>Loan #{{ drawer.row?.loanId }}</strong>
                <div class="row-muted">
                  Installment #{{ drawer.row?.installmentNo }} • ID {{ drawer.row?.installmentId }}
                </div>
              </div>

              <span class="status-pill" :class="statusClass(drawer.row?.status)">
                <i class="bx" :class="statusIcon(drawer.row?.status)"></i>
                {{ drawer.row?.status }}
              </span>
            </div>

            <div class="dgrid">
              <div class="kv">
                <span class="k">User</span>
                <strong class="v">{{ drawer.row?.name }}</strong>
                <span class="row-muted">{{ drawer.row?.phone }}</span>
              </div>

              <div class="kv">
                <span class="k">Due Date</span>
                <strong class="v">{{ drawer.row?.dueDate }}</strong>
                <span class="row-muted">Bucket: {{ bucketLabel(drawer.row?.bucket) }}</span>
              </div>

              <div class="kv">
                <span class="k">Amount</span>
                <strong class="v">₹ {{ fmt(drawer.row?.amount || 0) }}</strong>
                <span class="row-muted">Penalty: ₹ {{ fmt(drawer.row?.penalty || 0) }}</span>
              </div>

              <div class="kv">
                <span class="k">Payment</span>
                <strong class="v">{{ drawer.row?.status === "PAID" ? "Paid" : "Not Paid" }}</strong>
                <span class="row-muted">
                  {{ drawer.row?.status === "PAID" ? `Txn: ${drawer.row?.txnId}` : "-" }}
                </span>
              </div>
            </div>

            <div v-if="drawer.row?.status === 'OVERDUE'" class="warnBox">
              <i class="bx bx-error-circle"></i>
              <div>
                <strong>Overdue Alert</strong>
                <div class="row-muted">
                  This installment is overdue. Consider follow-up / reminder.
                </div>
              </div>
            </div>
          </div>

          <div class="qa">
            <button class="btn ghost" @click="handleReminder(drawer.row)">
              <i class="bx bx-bell"></i> Send Reminder
            </button>

            <button v-if="drawer.row?.status !== 'PAID'" class="btn primary" @click="openPaid(drawer.row)">
              <i class="bx bx-check-circle"></i> Mark Paid
            </button>

            <button v-if="drawer.row?.status === 'PAID'" class="btn primary" @click="openReceipt(drawer.row)">
              <i class="bx bx-receipt"></i> View Receipt
            </button>
          </div>
        </div>
      </aside>
    </div>

    <!-- MARK PAID MODAL -->
    <div v-if="paid.open" class="modal-backdrop" @click.self="closePaid">
      <div class="modal" v-motion-pop>
        <div class="modal-head">
          <div>
            <strong>Mark as Paid</strong>
            <div class="row-muted">Enter payment details for audit record.</div>
          </div>
          <button class="icon-mini" @click="closePaid"><i class="bx bx-x"></i></button>
        </div>

        <div class="modal-body">
          <div class="hint">
            <i class="bx bx-info-circle"></i>
            <span>
              Recording payment for <b>Loan #{{ paid.row?.loanId }}</b> installment
              <b>#{{ paid.row?.installmentNo }}</b>.
            </span>
          </div>

          <div class="field">
            <label>Transaction ID</label>
            <div class="input">
              <i class="bx bx-receipt"></i>
              <input v-model="paid.txnId" placeholder="TXN123..." />
            </div>
          </div>

          <div class="field">
            <label>Payment Method</label>
            <div class="input">
              <i class="bx bx-credit-card"></i>
              <select v-model="paid.method" class="select full">
                <option value="UPI">UPI</option>
                <option value="BANK_TRANSFER">Bank Transfer</option>
                <option value="CASH">Cash</option>
              </select>
            </div>
          </div>

          <div class="field">
            <label>Paid Date</label>
            <div class="input">
              <i class="bx bx-calendar"></i>
              <input v-model="paid.paidAt" type="date" />
            </div>
          </div>
        </div>

        <div class="modal-foot">
          <button class="btn ghost" @click="closePaid">Cancel</button>
          <button class="btn primary" :disabled="!canMarkPaid || paid.loading" @click="confirmPaid">
            <span v-if="paid.loading" class="loader" style="margin-right:8px;"></span>
            <i v-else class="bx bx-check-circle"></i>
            {{ paid.loading ? "Saving..." : "Save" }}
          </button>
        </div>
      </div>
    </div>

    <!-- RECEIPT MODAL -->
    <div v-if="receipt.open" class="modal-backdrop" @click.self="closeReceipt">
      <div class="modal big" v-motion-pop>
        <div class="modal-head">
          <div>
            <strong>Payment Receipt</strong>
            <div class="row-muted">Installment record for audit & print</div>
          </div>
          <button class="icon-mini" @click="closeReceipt"><i class="bx bx-x"></i></button>
        </div>

        <div class="modal-body">
          <div class="receipt">
            <div class="r-top">
              <div class="logo">
                <i class="bx bx-shield-quarter"></i>
              </div>
              <div>
                <strong>Loan App — Receipt</strong>
                <div class="row-muted">Generated for repayment confirmation</div>
              </div>
              <span class="badge success">PAID</span>
            </div>

            <div class="r-grid">
              <div class="kv">
                <span class="k">User</span>
                <strong class="v">{{ receipt.row?.name }}</strong>
                <span class="row-muted">{{ receipt.row?.phone }}</span>
              </div>

              <div class="kv">
                <span class="k">Loan ID</span>
                <strong class="v">#{{ receipt.row?.loanId }}</strong>
                <span class="row-muted">Installment #{{ receipt.row?.installmentNo }}</span>
              </div>

              <div class="kv">
                <span class="k">Amount</span>
                <strong class="v">₹ {{ fmt(receipt.row?.amount || 0) }}</strong>
                <span class="row-muted">Penalty: ₹ {{ fmt(receipt.row?.penalty || 0) }}</span>
              </div>

              <div class="kv">
                <span class="k">Transaction</span>
                <strong class="v">{{ receipt.row?.txnId }}</strong>
                <span class="row-muted">{{ receipt.row?.paidAt }} • {{ receipt.row?.method }}</span>
              </div>
            </div>

            <div class="r-foot">
              <span class="row-muted">This is a system generated receipt.</span>
              <span class="row-muted">© 2026 Loan App</span>
            </div>
          </div>
        </div>

        <div class="modal-foot">
          <button class="btn ghost" @click="toast.info('Print feature later')">
            <i class="bx bx-printer"></i> Print
          </button>
          <button class="btn primary" @click="closeReceipt">
            <i class="bx bx-check"></i> Done
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, reactive, ref, onMounted, watch } from "vue";
import { useToast } from "vue-toastification";

import {
  getRepaymentStats,
  getRepayments,
  markPaid,
  exportRepaymentsXl,
  sendReminder
} from "@/apis/repaycon";

const toast = useToast();

/* ✅ admin */
const ldata = ref(null);
const admin_id = ref("");

/* loaders */
const loading = reactive({
  stats: false,
  list: false,
  export: false,
});

/* filters */
const query = ref("");
const statusFilter = ref("");
const bucketFilter = ref("");
const sortBy = ref("DATE_ASC");

/* api rows */
const rows = ref([]);

/* stats from api */
const dueToday = ref(0);
const overdue = ref(0);
const collected30d = ref(0);
const pendingAmount = ref(0);

/* helpers */
const fmt = (n) => new Intl.NumberFormat("en-IN").format(Number(n || 0));

const statusClass = (s) => {
  if (s === "PAID") return "success";
  if (s === "DUE") return "primary";
  if (s === "OVERDUE") return "danger";
  return "primary";
};

const statusIcon = (s) => {
  if (s === "PAID") return "bx-check-circle";
  if (s === "DUE") return "bx-time";
  if (s === "OVERDUE") return "bx-error-circle";
  return "bx-info-circle";
};

const bucketLabel = (b) => {
  if (b === "TODAY") return "Due Today";
  if (b === "7D") return "Next 7 days";
  if (b === "30D") return "Next 30 days";
  return "-";
};

const bucketClass = (b) => {
  if (b === "TODAY") return "warning";
  if (b === "7D") return "primary";
  if (b === "30D") return "success";
  return "primary";
};

const bucketIcon = (b) => {
  if (b === "TODAY") return "bx-alarm";
  if (b === "7D") return "bx-calendar";
  if (b === "30D") return "bx-calendar-check";
  return "bx-info-circle";
};

/* filtering (client side fast UX) */
const filtered = computed(() => {
  let list = rows.value || [];

  const q = (query.value || "").toLowerCase().trim();
  if (q) {
    list = list.filter((r) => {
      return (
        String(r.loanId || "").toLowerCase().includes(q) ||
        String(r.userId || "").toLowerCase().includes(q) ||
        String(r.installmentId || "").toLowerCase().includes(q) ||
        String(r.name || "").toLowerCase().includes(q) ||
        String(r.phone || "").toLowerCase().includes(q)
      );
    });
  }

  if (statusFilter.value) list = list.filter((r) => r.status === statusFilter.value);
  if (bucketFilter.value) list = list.filter((r) => r.bucket === bucketFilter.value);

  // sorting
  if (sortBy.value === "DATE_ASC") list = list.slice().sort((a, b) => String(a.dueDate).localeCompare(String(b.dueDate)));
  if (sortBy.value === "DATE_DESC") list = list.slice().sort((a, b) => String(b.dueDate).localeCompare(String(a.dueDate)));
  if (sortBy.value === "AMOUNT_DESC") list = list.slice().sort((a, b) => (Number(b.amount) || 0) - (Number(a.amount) || 0));
  if (sortBy.value === "AMOUNT_ASC") list = list.slice().sort((a, b) => (Number(a.amount) || 0) - (Number(b.amount) || 0));

  return list;
});

/* ===========================
   API CALLS
   =========================== */
const fetchStats = async () => {
  try {
    loading.stats = true;

    const res = await getRepaymentStats(admin_id.value);
    if (!res || !res.status) return;

    dueToday.value = Number(res.due_today || 0);
    overdue.value = Number(res.overdue || 0);
    collected30d.value = Number(res.collected_30d || 0);
    pendingAmount.value = Number(res.pending_amount || 0);
  } catch (e) {
    // ignore
  } finally {
    loading.stats = false;
  }
};

const fetchList = async () => {
  try {
    loading.list = true;

    const res = await getRepayments({
      admin_id: admin_id.value,
      page_no: 0,
      keyword: query.value || "",     // server keyword filter
      statusFilter: statusFilter.value || "",
      bucketFilter: bucketFilter.value || "",
      sortBy: sortBy.value || "DATE_ASC",
    });

    if (!res || !res.status) {
      toast.error(res?.success || "Failed to load repayments");
      return;
    }

    const list = (res.records || []).map((x) => ({
      ...x,
      installmentId: x.installmentId ?? x.emi_id ?? "",
      loanId: x.loanId ?? x.emi_loan ?? "",
      installmentNo: x.installmentNo ?? x.emi_no ?? "",
      userId: x.userId ?? x.emi_user ?? "",

      name: x.name ?? "",
      phone: x.phone ?? "",

      dueDate: x.dueDate ?? "",
      bucket: x.bucket ?? "",

      amount: Number(x.amount || 0),
      penalty: Number(x.penalty || 0),
      status: String(x.status || "").toUpperCase(),

      txnId: x.txnId ?? "",
      paidAt: x.paidAt ?? "",
      method: x.method ?? "",
    }));

    rows.value = list;
  } catch (e) {
    toast.error("Request Failed");
  } finally {
    loading.list = false;
  }
};

const reloadAll = async () => {
  await fetchStats();
  await fetchList();
};

/* ✅ auto refresh list when filters change (server side fetch) */
watch([statusFilter, bucketFilter, sortBy], async () => {
  await fetchList();
});

/* ===========================
   Drawer
   =========================== */
const drawer = reactive({ open: false, row: null });
const openDrawer = (r) => {
  drawer.open = true;
  drawer.row = r;
};
const closeDrawer = () => {
  drawer.open = false;
  drawer.row = null;
};

/* ===========================
   Mark paid
   =========================== */
const paid = reactive({
  open: false,
  loading: false,
  row: null,
  txnId: "",
  method: "UPI",
  paidAt: "",
});

const openPaid = (r) => {
  paid.open = true;
  paid.row = r;
  paid.txnId = "";
  paid.method = "UPI";
  paid.paidAt = new Date().toISOString().slice(0, 10);
};

const closePaid = () => {
  paid.open = false;
  paid.row = null;
  paid.txnId = "";
  paid.method = "UPI";
  paid.paidAt = "";
};

const canMarkPaid = computed(() => !!(paid.txnId.trim() && paid.method && paid.paidAt));

const confirmPaid = async () => {
  try {
    if (!paid.row?.installmentId) return;

    paid.loading = true;

    const res = await markPaid({
      emi_id: paid.row.installmentId,
      admin_id: admin_id.value,
      txn_id: paid.txnId.trim(),
      method: paid.method,
      paid_at: paid.paidAt,
    });

    if (!res || !res.status) {
      toast.error(res?.success || "Failed to mark paid");
      return;
    }

    toast.success("Payment marked as paid ✅");
    closePaid();
    await reloadAll();
  } catch (e) {
    toast.error("Request Failed");
  } finally {
    paid.loading = false;
  }
};

/* ===========================
   Receipt
   =========================== */
const receipt = reactive({ open: false, row: null });
const openReceipt = (r) => {
  receipt.open = true;
  receipt.row = r;
};
const closeReceipt = () => {
  receipt.open = false;
  receipt.row = null;
};

/* ===========================
   Reminder
   =========================== */
const handleReminder = async (r) => {
  try {
    if (!r?.installmentId) return;

    const res = await sendReminder({
      admin_id: admin_id.value,
      emi_id: r.installmentId,
    });

    if (!res || !res.status) {
      toast.error(res?.success || "Reminder failed");
      return;
    }

    toast.success("Reminder sent ✅");
  } catch (e) {
    toast.error("Request Failed");
  }
};

/* ===========================
   Export XL
   =========================== */
const exportXl = async () => {
  try {
    loading.export = true;

    const res = await exportRepaymentsXl({
      admin_id: admin_id.value,
      keyword: query.value || "",
      statusFilter: statusFilter.value || "",
      bucketFilter: bucketFilter.value || "",
      sortBy: sortBy.value || "DATE_ASC",
    });

    if (!res) {
      toast.error("Export failed");
      return;
    }

    const blob = new Blob([res.data], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });

    const url = window.URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `repayments_${new Date().toISOString().slice(0, 10)}.xlsx`;
    document.body.appendChild(a);
    a.click();
    a.remove();

    toast.success("Export downloaded ✅");
  } catch (e) {
    toast.error("Export failed");
  } finally {
    loading.export = false;
  }
};

/* mount */
onMounted(async () => {
  try {
    ldata.value = JSON.parse(sessionStorage.getItem("ldata") || "{}");
    admin_id.value = ldata.value?.user_id || "1"; // ✅ replace later with admin_id column
    await reloadAll();
  } catch (e) {
    // ignore
  }
});
</script>


<style scoped>
.actions {
  display: flex;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;
}

.search-mini {
  height: 44px;
  position: relative;
  min-width: 280px;
}

.search-mini i {
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: rgba(148, 163, 184, 0.92);
  font-size: 18px;
}

.search-mini input {
  width: 100%;
  height: 44px;
  border-radius: 16px;
  border: 1px solid var(--line);
  padding: 0 12px 0 42px;
  background: rgba(255, 255, 255, 0.95);
  font-weight: var(--w-medium);
  font-size: 13px;
  outline: none;
}

.search-mini input:focus {
  border-color: rgba(46, 109, 231, 0.35);
  box-shadow: 0 0 0 4px rgba(46, 109, 231, 0.12);
}

.select {
  height: 44px;
  border-radius: 16px;
  border: 1px solid var(--line);
  padding: 0 12px;
  background: rgba(255, 255, 255, 0.95);
  font-weight: var(--w-medium);
  font-size: 13px;
  outline: none;
}

.select.full {
  width: 100%;
}

.loan {
  display: grid;
  gap: 4px;
}

.loan strong {
  font-weight: var(--w-black);
}

.usr {
  display: flex;
  align-items: center;
  gap: 10px;
}

.usr-meta {
  display: flex;
  flex-direction: column;
}

.usr-name {
  font-weight: var(--w-bold);
  font-size: 13px;
}

.date {
  display: grid;
  gap: 8px;
}

.pill.tiny {
  width: fit-content;
  display: inline-flex;
  gap: 8px;
  align-items: center;
  padding: 6px 10px;
  border-radius: 999px;
  font-size: 11px;
  font-weight: var(--w-bold);
  letter-spacing: 0.2px;
}

.pill.tiny i {
  font-size: 16px;
}

.pill.tiny.warning {
  background: var(--warning-10);
  color: var(--warning);
  border: 1px solid rgba(234, 88, 12, 0.16);
}

.pill.tiny.primary {
  background: var(--primary-10);
  color: var(--primary);
  border: 1px solid rgba(46, 109, 231, 0.16);
}

.pill.tiny.success {
  background: var(--success-10);
  color: var(--success);
  border: 1px solid rgba(22, 163, 74, 0.16);
}

.amt {
  display: grid;
  gap: 4px;
}

.amt strong {
  font-weight: var(--w-black);
}

.payMeta b {
  font-weight: var(--w-bold);
}

.table-head {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 14px;
  margin-bottom: 12px;
}

.table-title {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.table-title strong {
  font-weight: var(--w-bold);
}

.muted {
  color: var(--muted);
  font-size: 12px;
  font-weight: var(--w-medium);
}

.row-actions {
  display: inline-flex;
  gap: 8px;
  justify-content: flex-end;
}

.icon-mini {
  height: 38px;
  width: 38px;
  border-radius: 14px;
  border: 1px solid var(--line);
  background: rgba(255, 255, 255, 0.96);
  display: grid;
  place-items: center;
  cursor: pointer;
}

.icon-mini i {
  font-size: 18px;
  color: rgba(11, 18, 32, 0.88);
}

.icon-mini:hover {
  transform: translateY(-1px);
}

.empty {
  display: flex;
  gap: 12px;
  align-items: center;
}

.empty i {
  font-size: 26px;
  color: rgba(148, 163, 184, 0.9);
}

.list-cards {
  display: none;
  margin-top: 10px;
  gap: 12px;
}

.r-card {
  border: 1px solid var(--line);
  border-radius: var(--radius-xl);
  padding: 14px;
  background: rgba(255, 255, 255, 0.95);
  box-shadow: var(--shadow-sm);
}

.r-card .top {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  align-items: center;
}

.r-card .mid {
  margin-top: 10px;
  display: grid;
  gap: 10px;
}

.row2 {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
}

.paidBox {
  display: flex;
  gap: 10px;
  align-items: flex-start;
  padding: 12px;
  border-radius: 18px;
  background: rgba(22, 163, 74, 0.08);
  border: 1px solid rgba(22, 163, 74, 0.16);
}

.paidBox i {
  font-size: 18px;
  color: var(--success);
  margin-top: 1px;
}

.dangerRow td {
  background: rgba(239, 68, 68, 0.02);
}

.dangerCard {
  border-color: rgba(239, 68, 68, 0.18);
  background: rgba(239, 68, 68, 0.05);
}

.drawer-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(2, 6, 23, 0.55);
  backdrop-filter: blur(8px);
  z-index: 90;
  display: flex;
  justify-content: flex-end;
}

.drawer {
  width: min(520px, 92vw);
  height: 100%;
  background: rgba(255, 255, 255, 0.95);
  border-left: 1px solid var(--line);
  box-shadow: var(--shadow);
  padding: 16px;
  overflow: auto;
}

.drawer-head {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding-bottom: 12px;
  border-bottom: 1px solid var(--line);
}

.drawer-title strong {
  font-weight: var(--w-bold);
  font-size: 16px;
}

.drawer-body {
  margin-top: 14px;
  display: grid;
  gap: 14px;
}

.detail-card {
  border: 1px solid var(--line);
  border-radius: var(--radius-xl);
  background: rgba(246, 248, 252, 0.65);
  padding: 14px;
}

.dtop {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  align-items: center;
  margin-bottom: 12px;
}

.dgrid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}

.kv {
  border: 1px solid var(--line);
  background: rgba(255, 255, 255, 0.96);
  border-radius: 18px;
  padding: 10px;
}

.kv .k {
  font-size: 11px;
  color: var(--muted);
  font-weight: var(--w-medium);
}

.kv .v {
  margin-top: 6px;
  display: block;
  font-size: 13px;
  font-weight: var(--w-bold);
}

.warnBox {
  margin-top: 12px;
  display: flex;
  gap: 10px;
  align-items: flex-start;
  padding: 12px;
  border-radius: 18px;
  background: rgba(239, 68, 68, 0.08);
  border: 1px solid rgba(239, 68, 68, 0.18);
}

.warnBox i {
  font-size: 18px;
  color: var(--danger);
  margin-top: 2px;
}

.qa {
  display: flex;
  gap: 10px;
  flex-wrap: wrap;
}

.modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(2, 6, 23, 0.55);
  backdrop-filter: blur(8px);
  display: grid;
  place-items: center;
  z-index: 99;
  padding: 18px;
}

.modal {
  width: min(520px, 92vw);
  border-radius: 26px;
  background: rgba(255, 255, 255, 0.95);
  border: 1px solid var(--line);
  box-shadow: var(--shadow);
  padding: 16px;
}

.modal.big {
  width: min(820px, 94vw);
}

.modal-head {
  display: flex;
  justify-content: space-between;
  gap: 12px;
}

.modal-head strong {
  font-weight: var(--w-bold);
  font-size: 16px;
}

.modal-body {
  margin-top: 14px;
  display: grid;
  gap: 14px;
}

.modal-foot {
  margin-top: 14px;
  display: flex;
  gap: 10px;
  justify-content: flex-end;
}

.field label {
  font-size: 12px;
  font-weight: var(--w-semibold);
  color: rgba(15, 23, 42, 0.78);
  margin-bottom: 7px;
  display: block;
}

.input {
  position: relative;
}

.input i {
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  color: rgba(148, 163, 184, 0.92);
  font-size: 18px;
}

.input input {
  width: 100%;
  height: 48px;
  padding: 0 14px 0 44px;
  border: 1px solid var(--line);
  border-radius: 18px;
  outline: none;
  background: rgba(255, 255, 255, 0.96);
  font-size: 13px;
  font-weight: var(--w-medium);
}

.input input:focus {
  border-color: rgba(46, 109, 231, 0.35);
  box-shadow: 0 0 0 4px rgba(46, 109, 231, 0.12);
}

.notes {
  width: 100%;
  min-height: 110px;
  border-radius: 18px;
  border: 1px solid var(--line);
  background: rgba(255, 255, 255, 0.96);
  padding: 12px;
  outline: none;
  font-size: 13px;
  font-weight: var(--w-medium);
  resize: vertical;
}

.notes:focus {
  border-color: rgba(46, 109, 231, 0.35);
  box-shadow: 0 0 0 4px rgba(46, 109, 231, 0.12);
}

.hint {
  display: flex;
  gap: 10px;
  align-items: flex-start;
  padding: 12px;
  border-radius: 18px;
  background: rgba(46, 109, 231, 0.08);
  border: 1px solid rgba(46, 109, 231, 0.16);
  color: rgba(15, 23, 42, 0.74);
  font-size: 12px;
  font-weight: var(--w-medium);
  line-height: 1.45;
}

.hint i {
  font-size: 18px;
  color: var(--primary);
  margin-top: 1px;
}

.receipt {
  border: 1px solid var(--line);
  border-radius: var(--radius-xl);
  padding: 14px;
  background: rgba(246, 248, 252, 0.65);
}

.r-top {
  display: flex;
  align-items: center;
  gap: 12px;
  justify-content: space-between;
  margin-bottom: 12px;
}

.logo {
  height: 46px;
  width: 46px;
  border-radius: 16px;
  background: var(--primary);
  color: white;
  display: grid;
  place-items: center;
  box-shadow: 0 16px 22px rgba(46, 109, 231, 0.25);
}

.logo i {
  font-size: 22px;
}

.r-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
}

.r-foot {
  margin-top: 12px;
  display: flex;
  justify-content: space-between;
  gap: 10px;
}

@media (max-width: 960px) {
  .desktop-only {
    display: none;
  }
  .mobile-only {
    display: block;
  }
  .list-cards {
    display: grid;
  }

  .dgrid,
  .r-grid {
    grid-template-columns: 1fr;
  }
}
/* ===========================================
   ✅ REPAYMENTS MOBILE PATCH (END ONLY)
   Desktop CSS unchanged
   =========================================== */

@media (max-width: 980px) {
  /* page head -> stack */
  .page-head {
    flex-direction: column !important;
    align-items: flex-start !important;
    gap: 12px !important;
  }

  /* actions -> vertical full width */
  .actions {
    width: 100% !important;
    flex-direction: column !important;
    align-items: stretch !important;
    gap: 10px !important;
  }

  .search-mini {
    width: 100% !important;
    min-width: 0 !important;
  }

  .search-mini input {
    width: 100% !important;
  }

  .select {
    width: 100% !important;
  }

  .actions .btn.ghost {
    width: 100% !important;
    justify-content: center !important;
    height: 48px !important;
    border-radius: 18px !important;
  }

  /* SUMMARY metrics grid fix (prevent overflow) */
  .grid.cols-4 {
    grid-template-columns: 1fr 1fr !important;
    gap: 12px !important;
  }

  /* table head stack */
  .table-head {
    flex-direction: column !important;
    align-items: stretch !important;
    gap: 10px !important;
  }

  .table-head .btn.primary {
    width: 100% !important;
    justify-content: center !important;
    height: 48px !important;
    border-radius: 18px !important;
  }

  /* mobile cards internal row2 stack */
  .row2 {
    flex-direction: column !important;
    align-items: flex-start !important;
    gap: 10px !important;
  }

  .amt {
    width: 100% !important;
  }

  /* card actions -> vertical buttons full width */
  .card-actions {
    flex-direction: column !important;
    align-items: stretch !important;
  }

  .card-actions .btn.ghost {
    width: 100% !important;
    justify-content: center !important;
    height: 48px !important;
    border-radius: 18px !important;
  }

  /* drawer full width */
  .drawer {
    width: 100% !important;
    max-width: 520px !important;
  }

  /* drawer qa buttons stack */
  .qa {
    width: 100% !important;
    flex-direction: column !important;
    align-items: stretch !important;
  }

  .qa .btn {
    width: 100% !important;
    justify-content: center !important;
    height: 48px !important;
    border-radius: 18px !important;
  }

  /* modal -> full width + buttons stack */
  .modal-backdrop {
    padding: 12px !important;
  }

  .modal {
    width: 100% !important;
    max-width: 560px !important;
    padding: 14px !important;
    border-radius: 22px !important;
  }

  .modal.big {
    width: 100% !important;
    max-width: 860px !important;
  }

  .modal-foot {
    flex-direction: column-reverse !important;
    align-items: stretch !important;
    justify-content: flex-start !important;
  }

  .modal-foot .btn {
    width: 100% !important;
    justify-content: center !important;
    height: 48px !important;
    border-radius: 18px !important;
  }

  /* receipt header wrap */
  .r-top {
    flex-wrap: wrap !important;
    align-items: flex-start !important;
    justify-content: flex-start !important;
    gap: 10px !important;
  }

  .r-foot {
    flex-direction: column !important;
    align-items: flex-start !important;
    gap: 6px !important;
  }
}

/* ✅ extra small */
@media (max-width: 520px) {
  .grid.cols-4 {
    grid-template-columns: 1fr !important;
  }

  .r-card .top {
    flex-direction: column !important;
    align-items: flex-start !important;
    gap: 10px !important;
  }
}

</style>
